<script setup lang="ts">
import { nextTick, onMounted, ref, shallowRef, useTemplateRef, watch } from 'vue'
import type { PaginationQueryParams } from '@/@vxapp/@Enums/PaginationField'
import { PaginationField } from '@/@vxapp/@Enums/PaginationField'
import { PageMeta } from '@/@vxapp/@Enums/PageMeta'
import { ResponseCode } from '@/@vxapp/@Enums/PHP/HTTP/ResponseCode'
import { createLogger } from '@/@vxapp/@core/Logger/VxLog'
import { cloneReactiveData } from '@/@core/utils/cloneReactiveData'
import { useEventStore } from '@/@vxapp/Events/useEventStore'
import { setPageTitle } from '@/@core/utils/pageMetaUtils'
import EventDialog from '@/components/Dialogs/Events/EventDialog.vue'
import ServiceListToolbar from '@/components/ServiceLists/Shared/ServiceListToolbar.vue'
import EventsDatatable from '@/components/Tables/Events/EventsDatatable.vue'
import { DialogDisplayType } from '@/enums/dialog-display-type'

type EventData = Record<string, unknown>
type DisplayType = (typeof DialogDisplayType)[keyof typeof DialogDisplayType]

definePage({ meta: PageMeta.Events.List.Meta })
setPageTitle(PageMeta.Events.List.Title)

const log = createLogger('EventsListPage')
const route = useRoute()
const router = useRouter()
const eventStore = useEventStore() as any
const eventsTable = useTemplateRef<InstanceType<typeof EventsDatatable>>('eventsTable')
const eventData = ref<EventData | null>(null)
const displayType = shallowRef<DisplayType>(DialogDisplayType.CREATE)
const isDialogVisible = shallowRef(false)
const resultCount = shallowRef(0)
const defaults: PaginationQueryParams = { ...PaginationField.QueryParams, sort: '-id' }
const limitItems = [10, 25, 50, 100]

const sortItems = [
  { title: 'Newest first', value: '-id' },
  { title: 'Oldest first', value: '+id' },
]

function scalar(value: unknown) {
  const normalized = Array.isArray(value) ? value[0] : value

  return typeof normalized === 'string' || typeof normalized === 'number'
    ? String(normalized)
    : undefined
}

function positiveInteger(value: unknown, fallback: number) {
  const parsed = Number.parseInt(scalar(value) ?? '', 10)

  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback
}

function normalize(source: Record<string, unknown>): PaginationQueryParams {
  const limit = positiveInteger(source.limit, defaults.limit)
  const search = scalar(source.search)?.trim()

  return {
    ...defaults,
    page: positiveInteger(source.page, defaults.page),
    limit: limitItems.includes(limit) ? limit : defaults.limit,
    sort: scalar(source.sort) === '+id' ? '+id' : '-id',
    search: search || undefined,
  }
}

function same(left: PaginationQueryParams, right: PaginationQueryParams) {
  return (
    left.page === right.page &&
    left.limit === right.limit &&
    left.sort === right.sort &&
    left.search === right.search
  )
}

function applyRoute() {
  const incoming = normalize(route.query as Record<string, unknown>)
  const current = normalize(eventStore.queryParams ?? defaults)

  if (same(current, incoming)) return false
  eventStore.queryParams = incoming

  return true
}

function routeQuery(source: Record<string, unknown>) {
  const query = normalize(source)

  return {
    ...(query.page !== defaults.page ? { page: String(query.page) } : {}),
    ...(query.limit !== defaults.limit ? { limit: String(query.limit) } : {}),
    ...(query.sort !== defaults.sort ? { sort: query.sort } : {}),
    ...(query.search ? { search: query.search } : {}),
  }
}

async function fetchEvents() {
  await eventsTable.value?.fetchEvents()
}

async function updateQuery(patch: Partial<PaginationQueryParams>) {
  const next = normalize({ ...eventStore.queryParams, ...patch })

  eventStore.queryParams = next

  const query = routeQuery({ ...next })

  if (JSON.stringify(route.query) !== JSON.stringify(query)) await router.replace({ query })
  await nextTick()
  await fetchEvents()
}

async function handleSearch(searchQuery: string) {
  await updateQuery({ search: searchQuery || undefined, page: 1 })
}

async function handleClear() {
  await updateQuery({ ...defaults })
}

async function handleCreate() {
  const { template, responseCode } = await eventStore.fetchTemplate()

  if (responseCode !== ResponseCode.OK) return
  eventData.value = cloneReactiveData(template ?? {})
  displayType.value = DialogDisplayType.CREATE
  isDialogVisible.value = true
}

async function handleEdit(eventGUID: string) {
  const { event, responseCode } = await eventStore.fetch(eventGUID)

  if (responseCode !== ResponseCode.OK) {
    log.error('Failed to load event', { eventGUID, responseCode })

    return
  }
  eventData.value = cloneReactiveData(event ?? {})
  displayType.value = DialogDisplayType.EDIT
  isDialogVisible.value = true
}

async function handleSave(payload: EventData) {
  try {
    const response =
      displayType.value === DialogDisplayType.CREATE
        ? await eventStore.create(payload, true)
        : await eventStore.update(payload, true)

    if ([ResponseCode.OK, ResponseCode.CREATED].includes(response.responseCode)) await fetchEvents()
  } catch (error) {
    log.error('Failed to save event', error)
  }
}

async function handlePreview(eventGUID: string) {
  await router.push({ name: 'setup-events-preview-guid', params: { guid: eventGUID } })
}

async function handleSort(value: unknown) {
  await updateQuery({ sort: String(value), page: 1 })
}

async function handleLimit(value: unknown) {
  await updateQuery({ limit: Number(value), page: 1 })
}

async function handlePage(page: number) {
  await updateQuery({ page })
}

function handleLoaded(count: number) {
  resultCount.value = count
}

eventStore.queryParams = { ...defaults }
applyRoute()
onMounted(fetchEvents)

watch(
  () => route.query,
  async () => {
    if (!applyRoute()) return
    await nextTick()
    await fetchEvents()
  },
  { deep: true }
)
</script>

<template>
  <main
    class="events-workspace"
    data-testid="events-workspace"
  >
    <VCard class="events-workspace__hero">
      <VCardItem>
        <div class="events-workspace__hero-content">
          <div>
            <span class="events-workspace__eyebrow">Automation telemetry</span>
            <VCardTitle class="events-workspace__title">Events</VCardTitle>
            <VCardSubtitle class="events-workspace__subtitle">
              Monitor event definitions, required payloads, and their automation entry points.
            </VCardSubtitle>
          </div>
          <div class="events-workspace__metric">
            <span>Current view</span>
            <strong>{{ resultCount.toLocaleString() }}</strong>
          </div>
        </div>
      </VCardItem>
      <VDivider />
      <VCardActions class="events-workspace__actions">
        <VBtn
          color="primary"
          prepend-icon="tabler-plus"
          @click="handleCreate"
        >
          New event
        </VBtn>
        <VBtn
          prepend-icon="tabler-refresh"
          variant="tonal"
          @click="fetchEvents"
        >
          Refresh
        </VBtn>
      </VCardActions>
    </VCard>
    <ServiceListToolbar
      :query-params="eventStore.queryParams"
      :result-count="resultCount"
      :sort-items="sortItems"
      heading="Find the next event definition to manage"
      limit-label="Events per page"
      plural-label="events"
      search-label="Search events"
      search-placeholder="Name, source, type or payload"
      singular-label="event"
      sort-label="Sort events"
      @clear="handleClear"
      @limit-change="handleLimit"
      @refresh="fetchEvents"
      @search="handleSearch"
      @sort-change="handleSort"
    />

    <EventsDatatable
      ref="eventsTable"
      @edit="handleEdit"
      @loaded="handleLoaded"
      @page-change="handlePage"
      @preview="handlePreview"
    />
  </main>

  <EventDialog
    v-if="eventData"
    v-model:is-dialog-visible="isDialogVisible"
    :display-type="displayType"
    :event-data="eventData"
    @create="handleSave"
    @update="handleSave"
  />
</template>

<style scoped lang="scss">
.events-workspace {
  display: grid;
  min-inline-size: 0;
  gap: 1.5rem;
  font-feature-settings: 'cv01', 'ss03';
}

.events-workspace > * {
  box-sizing: border-box;
  inline-size: 100%;
  max-inline-size: 100%;
  min-inline-size: 0;
}

.events-workspace__hero {
  overflow: hidden;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 12px;
  background:
    radial-gradient(circle at 88% -30%, rgba(var(--v-theme-primary), 0.2), transparent 28rem),
    linear-gradient(110deg, rgba(var(--v-theme-surface-bright), 0.98), rgb(var(--v-theme-surface)));
  box-shadow: 0 18px 52px rgba(var(--v-theme-on-background), 0.06);
}

.events-workspace__hero-content {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 2rem;
  padding-block: 0.4rem;
}

.events-workspace__eyebrow {
  color: rgb(var(--v-theme-primary));
  font-size: 0.6875rem;
  font-weight: 590;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.events-workspace__title {
  margin-block-start: 0.35rem;
  font-size: clamp(1.75rem, 3vw, 2.35rem);
  font-weight: 590;
  letter-spacing: -0.04em;
}

.events-workspace__subtitle {
  max-inline-size: 48rem;
  margin-block-start: 0.65rem;
  white-space: normal;
}

.events-workspace__metric {
  display: grid;
  min-inline-size: 7rem;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 8px;
  background: rgba(var(--v-theme-surface), 0.7);
  gap: 0.2rem;
  padding: 0.75rem 0.9rem;
}

.events-workspace__metric span {
  color: rgba(var(--v-theme-on-surface), 0.56);
  font-size: 0.6875rem;
  text-transform: uppercase;
}

.events-workspace__metric strong {
  font-size: 1.25rem;
  font-weight: 590;
}

.events-workspace__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.85rem 1rem;
}

@media (max-width: 599px) {
  .events-workspace {
    gap: 1rem;
  }

  .events-workspace__hero-content {
    align-items: stretch;
    flex-direction: column;
    gap: 1rem;
  }

  .events-workspace__metric {
    min-inline-size: 0;
  }

  .events-workspace__actions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .events-workspace__actions :deep(.v-btn) {
    min-block-size: 3rem;
    inline-size: 100%;
  }
}
</style>
